import { Button, Form, Modal, Select, Spin } from "antd";
import FormItem from "antd/es/form/FormItem";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { useContext, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  getJsonByGeneralInventoryControl,
  reCalculateInventoryControl,
  ResponseJsonGenerator,
} from "api/generated";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useClient } from "hooks/useClient";
import DistributionPointSelected from "context/MyDistributionPoint";

export enum REPORT_TYPE {
  DIARIO = "DIARIO",
  MENSUAL = "MENSUAL",
}

interface CreateJsonReportProps {
  onClose: (json?: ResponseJsonGenerator) => void;
  selectedReportType: REPORT_TYPE;
  setSelectedReportType: (value: REPORT_TYPE) => void;
  defaultDates: [dayjs.Dayjs, dayjs.Dayjs];
  updateDates: (rangeTimeValue: [dayjs.Dayjs, dayjs.Dayjs]) => void;
}

export default function CreateJsonReport({
  onClose,
  selectedReportType,
  setSelectedReportType,
  defaultDates,
  updateDates,
}: CreateJsonReportProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);

  const [rangeTimeValue, setRangeTimeValue] =
    useState<[Dayjs, Dayjs]>(defaultDates);

  const defaultType = useMemo(
    () =>
      selectedReportType === "DIARIO"
        ? DateFilterType.DAY
        : DateFilterType.MONTH,
    [selectedReportType]
  );

  function handleOnClose() {
    if (isLoading) {
      return;
    }
    onClose();
  }

  function handleCreateJson() {
    if (!distributionPoint || isLoading) {
      return;
    }
    setIsLoading(true);
    const startDate =
      defaultType === DateFilterType.DAY
        ? rangeTimeValue[0].format("YYYY-MM-DD")
        : rangeTimeValue[0].endOf("month").format("YYYY-MM-DD");
    reCalculateInventoryControl({
      client,
      body: {
        startDate,
        reportType: "Regeneración de GIC",
        idSubproduct: 51,
        distributionPointId: distributionPoint.id,
      },
    })
      .then(({ data }) => {
        updateDates(rangeTimeValue);

        if (!data || data[0].idGeneralInventoryControl === undefined) {
          // Cerrar todo, no se genero el control de existencias
          setIsLoading(false);
          onClose();
          return;
        }

        getJsonByGeneralInventoryControl({
          client,
          path: {
            idGeneralInventoryControl: data[0].idGeneralInventoryControl,
            reportType: selectedReportType,
          },
        })
          .then(({ data }) => {
            onClose(data);
          })
          .catch((err) =>
            handleAxiosError(err, "No fue posible recuperar el reporte json")
          )
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((err) => {
        handleAxiosError(err, "No fue posible generar el reporte json");
        setIsLoading(false);
      });
  }

  return (
    <Modal
      title="Generación de Reporte CV (JSON)"
      open={true}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
      }}
      onCancel={handleOnClose}
      footer={[
        <Button
          className="back-button"
          key="cancel"
          onClick={handleOnClose}
          disabled={isLoading}
        >
          Cerrar
        </Button>,
        <Button
          type="primary"
          key="onCreate"
          onClick={handleCreateJson}
          disabled={isLoading}
          loading={isLoading}
        >
          Generar Reporte
        </Button>,
      ]}
      onOk={handleCreateJson}
      centered={true}
    >
      <Spin spinning={isLoading}>
        <Form layout="vertical">
          <FormItem label="Tipo reporte" required>
            <Select
              style={{ width: "100%" }}
              value={selectedReportType}
              onChange={(value) => setSelectedReportType(value)}
              options={[
                { value: REPORT_TYPE.DIARIO, label: REPORT_TYPE.DIARIO },
                { value: "MENSUAL", label: "MENSUAL" },
              ]}
            />
          </FormItem>
          <FormItem label="Fecha del reporte a generar" required>
            <DateRangePicker
              config={{
                allowedTypes: [defaultType],
                defaultType,
                forceType: defaultType,
                showQuickFilters: true,
              }}
              rangeTimeValue={rangeTimeValue}
              setRangeTimeValue={setRangeTimeValue}
            />
          </FormItem>
        </Form>
      </Spin>
    </Modal>
  );
}
